import type { LabelFields, LabelSheet } from '@aeris/printing'

/**
 * O molde de etiqueta DESTE computador — F124.
 *
 * Decisão do dono: a configuração é do computador, não da loja. Pelo mesmo
 * motivo de `remembered-paper.ts` — o adesivo que está na gaveta é uma
 * propriedade da máquina e da impressora ao lado dela, não de quem está em pé
 * ali. A loja que usa 50×30 mm no balcão e 100×50 mm no depósito quer as duas
 * respostas ao mesmo tempo, e por loja daria uma só.
 *
 * Consequência aceita: trocar de computador é reconfigurar. É uma tela de seis
 * campos, feita uma vez, contra uma tabela nova, uma rota nova e a pergunta "de
 * qual filial é esta impressora" para sempre.
 */

export type LabelTemplate = LabelSheet & LabelFields

const STORAGE_KEY = 'aeris.label-template'

/**
 * 50×30 mm em uma coluna.
 *
 * O rolo mais vendido no Brasil para etiqueta de produto, e o que sai errado
 * de menos: numa bobina de uma coluna, `@page` e o driver têm menos com o que
 * discordar. Quem tiver outro adesivo muda uma vez; quem não souber o que tem
 * imprime a etiqueta de teste.
 */
export const DEFAULT_LABEL_TEMPLATE: LabelTemplate = {
  widthMm: 50,
  heightMm: 30,
  marginMm: 2,
  columns: 1,
  gapMm: 0,
  showName: true,
  showBarcode: true,
  showPrice: true,
  showSku: false,
}

/** Fora disto o navegador aceita e a impressora não. */
const LIMITS = {
  widthMm: [10, 210],
  heightMm: [10, 297],
  marginMm: [0, 10],
  columns: [1, 6],
  gapMm: [0, 20],
} as const

const clampNumber = (
  value: unknown,
  [least, most]: readonly [number, number],
  fallback: number
): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(most, Math.max(least, value))
}

const readBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback

/**
 * Lê o molde gravado, **campo a campo**.
 *
 * `JSON.parse` devolve `any`, e um `as LabelTemplate` aqui seria aceitar o que
 * quer que esteja no `localStorage` — inclusive o molde de uma versão anterior,
 * a que faltavam campos, ou uma altura zero que produz um adesivo invisível.
 * Cada campo que não convencer volta ao padrão em vez de derrubar a tela.
 */
export const readLabelTemplate = (): LabelTemplate => {
  let stored: unknown
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return DEFAULT_LABEL_TEMPLATE
    stored = JSON.parse(raw)
  } catch {
    // Modo anônimo, cota cheia, ou JSON quebrado pela metade. O padrão imprime.
    return DEFAULT_LABEL_TEMPLATE
  }

  if (typeof stored !== 'object' || stored === null) {
    return DEFAULT_LABEL_TEMPLATE
  }
  const raw = stored as Record<string, unknown>
  const fallback = DEFAULT_LABEL_TEMPLATE

  return {
    widthMm: clampNumber(raw.widthMm, LIMITS.widthMm, fallback.widthMm),
    heightMm: clampNumber(raw.heightMm, LIMITS.heightMm, fallback.heightMm),
    marginMm: clampNumber(raw.marginMm, LIMITS.marginMm, fallback.marginMm),
    columns: Math.round(
      clampNumber(raw.columns, LIMITS.columns, fallback.columns)
    ),
    gapMm: clampNumber(raw.gapMm, LIMITS.gapMm, fallback.gapMm),
    showName: readBoolean(raw.showName, fallback.showName),
    showBarcode: readBoolean(raw.showBarcode, fallback.showBarcode),
    showPrice: readBoolean(raw.showPrice, fallback.showPrice),
    showSku: readBoolean(raw.showSku, fallback.showSku),
  }
}

export const rememberLabelTemplate = (template: LabelTemplate): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(template))
  } catch {
    // Gravar o molde é conveniência; imprimir é o trabalho. Uma cota cheia não
    // pode impedir a etiqueta de sair.
  }
}
